// src/pages/services/AIMLSolutions.jsx - Premium AI/ML Services
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Brain, Bot, Eye, TrendingUp, ArrowLeft, ArrowRight, Zap, BarChart3, Lock, ExternalLink } from 'lucide-react';
import RelatedServices from '../../components/RelatedServices';


export default function AIMLSolutions() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const services = [
    {
      icon: <Brain className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Machine Learning Models',
      description: 'Custom ML models trained on your data for specific use cases',
      features: ['Predictive Analytics', 'Classification Models', 'Regression Analysis', 'Model Optimization']
    },
    {
      icon: <Bot className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Natural Language Processing',
      description: 'NLP solutions for text analysis and language understanding',
      features: ['Chatbots', 'Sentiment Analysis', 'Text Summarization', 'Language Translation']
    },
    {
      icon: <Eye className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Computer Vision',
      description: 'Image and video analysis powered by deep learning',
      features: ['Object Detection', 'Face Recognition', 'OCR', 'Image Classification']
    },
    {
      icon: <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Predictive Analytics',
      description: 'Forecast trends and make data-driven decisions',
      features: ['Sales Forecasting', 'Customer Churn', 'Demand Planning', 'Risk Prediction']
    }
  ];

  const technologies = [
    { name: 'TensorFlow', icon: '🧠' },
    { name: 'PyTorch', icon: '🔥' },
    { name: 'Scikit-learn', icon: '📊' },
    { name: 'OpenCV', icon: '👁️' },
    { name: 'Hugging Face', icon: '🤗' },
    { name: 'Keras', icon: '⚡' }
  ];

  const aiCapabilities = [
    'Deep Learning', 'Neural Networks', 'Reinforcement Learning',
    'Transfer Learning', 'GANs', 'Time Series Analysis'
  ];

  const pricingPlans = [
    {
      name: 'POC/MVP',
      price: 12999,
      monthlySupport: 499,
      color: 'purple',
      badge: null,
      features: [
        'Basic ML Model Development',
        'Data Analysis & Exploration',
        'Proof of Concept Delivery',
        '2 Months Support',
        'Model Documentation',
        'Performance Metrics Report'
      ]
    },
    {
      name: 'Production Model',
      price: 34999,
      monthlySupport: 1499,
      color: 'blue',
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        'Advanced ML/Deep Learning',
        'Production-Ready Code',
        'API Integration Setup',
        '6 Months Support Included',
        'Priority Technical Support',
        'Model Monitoring & Optimization',
        'Quarterly Performance Reviews'
      ]
    },
    {
      name: 'Enterprise AI',
      price: null,
      monthlySupport: 5000,
      color: 'emerald',
      badge: null,
      features: [
        'Custom AI Solution Development',
        'Dedicated Team (2-3 Data Scientists)',
        '24/7 Premium Support',
        'Custom SLA & Infrastructure',
        'Real-time Monitoring Dashboard',
        'Continuous Model Improvement',
        'Full Stack Integration'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-purple-600 to-blue-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base font-medium">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 leading-tight">
                AI & Machine Learning Solutions
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-purple-100 mb-6 sm:mb-8 leading-relaxed">
                Transform your business with custom AI models. From predictive analytics to computer vision, we build intelligent systems that drive competitive advantage.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base shadow-lg">
                  Start AI Project
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View AI Projects
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop"
                alt="AI Machine Learning"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our AI/ML Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              Comprehensive AI and machine learning services tailored to your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center text-purple-600 mb-3 sm:mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{service.description}</p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Capabilities Section - Focused on What We Can Build */}
      <section className="py-12 sm:py-20 lg:py-24 bg-gradient-to-br from-purple-600/10 via-blue-600/10 to-purple-600/10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl lg:max-w-7xl mx-auto">
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-12 shadow-2xl border border-purple-200/50 dark:border-purple-800/50">
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8 lg:gap-12">
              {/* Left Content - Capabilities & Approach */}
              <div className="lg:w-1/2">
                <div className="text-center lg:text-left">
                  <Brain className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-purple-600 mx-auto lg:mx-0 mb-4 lg:mb-6 opacity-75 animate-pulse" />
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-4 lg:mb-6">
                    AI Solutions For Your Business
                  </h2>
                  <p className="text-base sm:text-lg lg:text-xl text-gray-700 dark:text-gray-300 mb-6 lg:mb-8 leading-relaxed">
                    We build custom AI and machine learning solutions tailored to your specific needs. 
                    From predictive analytics to computer vision systems, 
                    <strong className="font-semibold text-purple-600 dark:text-purple-400 block lg:inline"> we use cutting-edge technology to solve your challenges</strong>. 
                    Share your requirements, and we'll develop the perfect AI solution.
                  </p>
                </div>

                {/* AI Capabilities - Desktop */}
                <div className="hidden lg:grid grid-cols-2 gap-6 mb-8">
                  <div className="group p-6 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-2xl border border-purple-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div className="flex items-center gap-4">
                      <Brain className="w-12 h-12 text-purple-600 group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Machine Learning</h3>
                        <div className="space-y-1">
                          <div className="text-lg font-medium text-gray-600 dark:text-gray-400">Predictive Models</div>
                          <div className="text-lg font-bold text-blue-600">Data Analysis</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="group p-6 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-2xl border border-purple-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div className="flex items-center gap-4">
                      <Bot className="w-12 h-12 text-purple-600 group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Natural Language</h3>
                        <div className="space-y-1">
                          <div className="text-lg font-medium text-gray-600 dark:text-gray-400">Chatbots & Analysis</div>
                          <div className="text-lg font-bold text-blue-600">Text Processing</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop CTA Buttons */}
                <div className="hidden lg:flex flex-col gap-4">
                  <Link to="/contact" className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-2xl font-black text-lg shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-center tracking-wide flex items-center justify-center gap-3">
                    🚀 Start AI Project
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link to="/portfolio" className="px-8 py-4 border-2 border-purple-600 text-purple-600 dark:text-purple-400 rounded-2xl font-black text-lg hover:bg-purple-600 hover:text-white transition-all duration-300 text-center tracking-wide flex items-center justify-center gap-3">
                    View Our Capabilities
                    <ExternalLink className="w-5 h-5" />
                  </Link>
                </div>
              </div>

              {/* Right Content - AI Solutions We Build */}
              <div className="lg:w-1/2">
                <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-2xl border border-purple-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <Eye className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 text-purple-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-3 text-center">Computer Vision</h3>
                    <div className="space-y-1 sm:space-y-2 text-center">
                      <div className="text-lg sm:text-xl lg:text-xl font-bold text-purple-600">Image Analysis</div>
                      <div className="text-base sm:text-lg font-medium text-blue-600">Object Recognition</div>
                    </div>
                  </div>
                  
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-2xl border border-purple-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <TrendingUp className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 text-purple-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-3 text-center">Predictive Analytics</h3>
                    <div className="space-y-1 sm:space-y-2 text-center">
                      <div className="text-lg sm:text-xl lg:text-xl font-bold text-purple-600">Data Forecasting</div>
                      <div className="text-base sm:text-lg font-medium text-blue-600">Business Insights</div>
                    </div>
                  </div>
                  
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-2xl border border-purple-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 lg:col-span-2">
                    <div className="flex flex-col sm:flex-row items-center gap-4 lg:gap-6">
                      <div className="text-4xl">🛠️</div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2">Custom AI Development</h3>
                        <div className="text-xl sm:text-2xl lg:text-2xl font-bold text-purple-600">Tailored to Your Needs</div>
                        <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">Tell us what you need, we'll build it</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Development Process - Mobile */}
                <div className="mt-6 lg:hidden">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="group p-4 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-xl border border-purple-200/50">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">Planning</div>
                        <div className="text-sm text-purple-600">Requirements & Strategy</div>
                      </div>
                    </div>
                    <div className="group p-4 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-xl border border-purple-200/50">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">Development</div>
                        <div className="text-sm text-purple-600">Model Training & Testing</div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile/Tablet CTA Buttons */}
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <Link to="/contact" className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-2xl font-black text-sm sm:text-base shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-center tracking-wide">
                      🚀 Start AI Project
                    </Link>
                    <Link to="/portfolio" className="px-6 sm:px-8 py-3 sm:py-4 border-2 sm:border-4 border-purple-600 text-purple-600 dark:text-purple-400 rounded-2xl font-black text-sm sm:text-base hover:bg-purple-600 hover:text-white transition-all duration-300 text-center tracking-wide">
                      View Our Capabilities
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Technologies - Desktop Only */}
            <div className="hidden lg:grid grid-cols-4 gap-4 mt-12 pt-8 border-t border-purple-200/50 dark:border-purple-800/50">
              {[
                { label: 'TensorFlow/PyTorch', value: 'ML Frameworks', icon: '🧠', description: 'Deep Learning' },
                { label: 'Computer Vision', value: 'OpenCV & More', icon: '👁️', description: 'Image Analysis' },
                { label: 'NLP', value: 'Text Processing', icon: '📝', description: 'Language Models' },
                { label: 'Data Analysis', value: 'Scikit-learn', icon: '📊', description: 'ML Algorithms' }
              ].map((tech, index) => (
                <div key={index} className="text-center p-4 bg-gradient-to-br from-purple-50/50 to-blue-50/50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl hover:shadow-lg transition-all duration-300">
                  <div className="text-3xl mb-2">{tech.icon}</div>
                  <div className="text-2xl font-black text-purple-600">{tech.value}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{tech.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">{tech.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI Capabilities */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            AI Capabilities We Master
          </h2>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {aiCapabilities.map((capability, index) => (
              <div key={index} className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-xs sm:text-base shadow-lg hover:scale-105 transition-transform cursor-default">
                {capability}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Technologies & Frameworks
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-lg shadow-lg text-center hover:scale-105 transition border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-3xl sm:text-5xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform">{tech.icon}</div>
                <h3 className="font-semibold text-xs sm:text-base text-gray-900 dark:text-white">{tech.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Our AI Development Process
          </h2>
          <div className="grid md:grid-cols-5 gap-3 sm:gap-6">
            {[
              { step: '1', title: 'Discovery', desc: 'Understand your problem' },
              { step: '2', title: 'Data Strategy', desc: 'Plan data collection' },
              { step: '3', title: 'Model Training', desc: 'Build & optimize' },
              { step: '4', title: 'Validation', desc: 'Test performance' },
              { step: '5', title: 'Deployment', desc: 'Go to production' }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-full flex items-center justify-center text-lg sm:text-2xl font-black mx-auto mb-2 sm:mb-4 shadow-lg group-hover:scale-110 transition-transform">
                  {item.step}
                </div>
                <h3 className="text-sm sm:text-lg font-bold mb-1 sm:mb-2 text-gray-900 dark:text-white">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              AI/ML Project Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Transparent pricing for AI solutions. From MVP to enterprise-scale systems.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {pricingPlans.map((plan, idx) => {
              const colorMap = {
                purple: { bg: 'from-purple-600 to-blue-600', light: 'from-purple-50 to-blue-50', badge: 'bg-purple-500', hover: 'hover:border-purple-200' },
                blue: { bg: 'from-blue-600 to-cyan-600', light: 'from-blue-50 to-cyan-50', badge: 'bg-blue-500', hover: 'hover:border-blue-200' },
                emerald: { bg: 'from-emerald-600 to-teal-600', light: 'from-emerald-50 to-teal-50', badge: 'bg-emerald-500', hover: 'hover:border-emerald-200' }
              };
              const colors = colorMap[plan.color];

              return (
                <div
                  key={idx}
                  className={`group relative ${plan.featured ? 'md:scale-105' : ''} bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col ${colors.hover} dark:hover:border-gray-600/50 ${plan.featured ? 'border-2 border-blue-500 dark:border-blue-600' : ''}`}
                >
                  {/* Glow effect for featured */}
                  {plan.featured && (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 dark:from-blue-600/20 dark:to-cyan-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
                  )}

                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`bg-gradient-to-r ${colors.bg} text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg`}>
                        {plan.badge} ⭐
                      </div>
                    </div>
                  )}

                  <h3 className={`text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 ${plan.featured ? 'mt-4 sm:mt-6' : ''}`}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                    {plan.name === 'POC/MVP' && 'Perfect to validate ideas'}
                    {plan.name === 'Production Model' && 'Best for business implementation'}
                    {plan.name === 'Enterprise AI' && 'Custom solutions at scale'}
                  </p>

                  {/* Price */}
                  <div className="mb-8">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'purple' ? '#9333ea, #0284c7' : plan.color === 'blue' ? '#0284c7, #06b6d4' : '#059669, #0d9488'})`}}>
                            ${plan.price.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'purple' ? '#9333ea, #0284c7' : plan.color === 'blue' ? '#0284c7, #06b6d4' : '#059669, #0d9488'})`}}>
                            ${plan.monthlySupport.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'purple' ? '#9333ea, #0284c7' : plan.color === 'blue' ? '#0284c7, #06b6d4' : '#059669, #0d9488'})`}}>
                          ${plan.monthlySupport.toLocaleString()}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-semibold">/month retainer</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Custom scoping available</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${plan.color === 'purple' ? '#e0e7ff' : plan.color === 'blue' ? '#cffafe' : '#d1fae5'}`}}>
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 font-bold" style={{color: `${plan.color === 'purple' ? '#6d28d9' : plan.color === 'blue' ? '#0369a1' : '#047857'}`}} />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to="/contact"
                    className={`w-full px-6 py-3 sm:py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1 text-center ${
                      plan.featured
                        ? `bg-gradient-to-r ${colors.bg} text-white hover:shadow-2xl`
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {plan.name === 'Enterprise AI' ? 'Let\'s Talk' : 'Get Started'}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* All Plans Include */}
          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: '📊', text: 'Data Analysis' },
                { icon: '🔧', text: 'Model Optimization' },
                { icon: '📚', text: 'Full Documentation' },
                { icon: '📈', text: 'Performance Metrics' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-2xl border border-purple-200/50 dark:border-purple-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">AI Project Frequently Asked Questions</h3>
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  q: 'How long does an AI project typically take?',
                  a: 'POC projects: 4-8 weeks. Production models: 3-4 months. Enterprise: Custom timeline based on scope. We provide detailed timelines during the discovery phase.'
                },
                {
                  q: 'What data do I need to provide?',
                  a: 'For ML models, you need historical data relevant to your problem. We help with data collection, cleaning, and preparation. Minimum 500-1000 samples recommended for basic models.'
                },
                {
                  q: 'Can you integrate AI into my existing systems?',
                  a: 'Yes! We provide REST APIs and SDKs for seamless integration. Our solutions work with your existing tech stack - databases, CRM, ERP, cloud platforms, etc.'
                },
                {
                  q: 'What happens after deployment?',
                  a: 'We provide ongoing monitoring, model retraining, performance optimization, and support. Maintenance plans ensure your AI stays accurate as your data evolves.'
                },
                {
                  q: 'Is my data secure?',
                  a: 'Absolutely. We implement enterprise-grade security, data encryption, compliance with GDPR/HIPAA, and secure cloud infrastructure. Your data is never shared.'
                }
              ].map((faq, idx) => (
                <details key={idx} className="group cursor-pointer">
                  <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span className="text-sm sm:text-base text-left pr-4">{faq.q}</span>
                    <span className="group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
                  </summary>
                  <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Brain className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-pulse" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Ready to Build Intelligent Systems?
          </h2>
          <p className="text-base sm:text-xl text-purple-100 mb-6 sm:mb-8 px-2">
            Transform your business with AI. Let's discuss how machine learning can solve your challenges and unlock new opportunities.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-purple-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>🚀 Schedule AI Consultation</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
      <div className="mt-16">
      <RelatedServices currentService="ai-ml-solutions" />
      </div>
    </div>
  );
}